import React, { useEffect, useRef, useState } from "react";
import { CuttingProgress } from "../Controls/Progress/CuttingProgress";
import { ReactComponent as ImagePlay } from '../../public/images/svg/ImagePlay.svg';
import { ReactComponent as ImagePause } from '../../public/images/svg/ImagePause.svg';
import { ReactComponent as ImageRefresh } from '../../public/images/svg/ImageRefresh.svg';
import { ReactComponent as ImageSpeakerMute } from '../../public/images/svg/ImageSpeakerMute.svg';
import { ReactComponent as ImageSpeakerSound } from '../../public/images/svg/ImageSpeakerSound.svg';
import { GlyphButton } from '../Controls/Button/GlyphButton';
import { formatTime } from '../Utils/ConversionUtils'
import "./css/VideoPreview.css";

export interface VideoPreviewProps {
    videoUrl: string | undefined;
    aspectRatio: number; // width / height
}

export const VideoPreview: React.FC<VideoPreviewProps> = ({
    videoUrl,
    aspectRatio
}) => {
    const [videoLoaded, setVideoLoaded] = useState(false);

    useEffect(() => {
        setVideoLoaded(false);
    }, [videoUrl]);

    const videoRef = useRef<HTMLVideoElement | null>(null);
    const videoWrapperRef = useRef<HTMLDivElement | null>(null);

    const [canPlay, setCanPlay] = useState(false);
    const [canPause, setCanPause] = useState(false);
    const [canReplay, setCanReplay] = useState(false);
    const [isMuted, setIsMuted] = useState(true);
    const [progress, setProgress] = useState(0); // 0 → 1
    const [currentTime, setCurrentTime] = useState(0);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handlePlay = () => {
            setCanPlay(false)
            setCanPause(true);
            setCanReplay(false);
        };

        const handlePause = () => {
            setCanPlay(true)
            setCanPause(false);
            setCanReplay(false);
        };

        const handleEnded = () => {
            setCanPlay(false)
            setCanPause(false);
            setCanReplay(true);
        };

        const handleTimeUpdate = () => {
            if (video.duration > 0) {
                setProgress(video.currentTime / video.duration);
                setCurrentTime(Math.min(video.currentTime, video.duration));
            }
        };

        video.addEventListener("play", handlePlay);
        video.addEventListener("pause", handlePause);
        video.addEventListener("ended", handleEnded);
        video.addEventListener("timeupdate", handleTimeUpdate);

        if (aspectRatio && videoWrapperRef.current) {
            videoWrapperRef.current.style.setProperty('--aspect-ratio', `${aspectRatio}`);
        }

        return () => {
            video.removeEventListener("play", handlePlay);
            video.removeEventListener("pause", handlePause);
            video.removeEventListener("ended", handleEnded);
            video.removeEventListener("timeupdate", handleTimeUpdate);
        };
    }, [videoUrl]);


    const handlePlay = () => videoRef.current?.play();
    const handlePause = () => videoRef.current?.pause();
    const handleReplay = () => {
        if (videoRef.current) {
            videoRef.current.currentTime = 0;
            videoRef.current.play();
        }
    };

    const showPlaceholder = !videoUrl || !videoLoaded;

    return (
        <div className="vp-body">
            <div className="vp-body-container">
                <div
                    ref={videoWrapperRef}
                    className="vp-preview-wrapper">
                    {showPlaceholder && (
                        <div className="vp-placeholder">
                            <CuttingProgress text="Loading..." />
                        </div>
                    )}

                    <div className="vp-video-container">
                        <video
                            ref={videoRef}
                            className={`vp-video`}
                            src={videoUrl}
                            onLoadedData={() => {
                                setVideoLoaded(true);
                            }}
                            playsInline
                            autoPlay
                            controls={false}
                            muted={isMuted}
                        />
                        <div className="vp-in-image-controls">
                            <GlyphButton ariaLabel="toggle-sound-mute"
                                onClick={() => { setIsMuted(val => { return !val }) }}
                                IconComponent={isMuted
                                    ? <ImageSpeakerMute width={16} height={16} viewBox="0 0 32 32" />
                                    : <ImageSpeakerSound width={16} height={16} viewBox="0 0 32 32" />
                                } />
                        </div>
                    </div>
                </div>
                <div className="vp-progress-container">
                    <div className="vp-progress-bar">
                        <div className="vp-progress-fill" style={{ width: `${progress * 100}%` }} />
                        <div
                            className="vp-progress-ball"
                            style={{ left: `${progress * 100}%` }}>
                            <div>
                                <span>{formatTime(currentTime)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="vp-footer">
                <div className="vp-controls">
                    <GlyphButton ariaLabel="play-video"
                        onClick={handlePlay}
                        label="Play"
                        labelPosition="right"
                        disabled={!canPlay}
                        IconComponent={<ImagePlay height={16} width={16} viewBox="0 0 32 32" />} />
                    <GlyphButton ariaLabel="pause-video"
                        onClick={handlePause}
                        label="Pause"
                        labelPosition="right"
                        disabled={!canPause}
                        IconComponent={<ImagePause height={16} width={16} viewBox="0 0 32 32" />} />
                    <GlyphButton ariaLabel="replay-video"
                        onClick={handleReplay}
                        label="Replay"
                        labelPosition="right"
                        disabled={!canReplay}
                        IconComponent={<ImageRefresh height={16} width={16} viewBox="0 0 34 34" />} />
                </div>
            </div>
        </div>
    );
};
